/**
 * MAISON KRAFT - Browser Compatibility Checker for Vapi
 * Détecte les navigateurs supportés et affiche un avertissement si nécessaire
 */

(function() {
  'use strict';

  // ============================================
  // NAVIGATEURS SUPPORTÉS (Web Audio API)
  // ============================================
  const SUPPORTED_BROWSERS = {
    chrome: { min: 14, name: 'Google Chrome' },
    firefox: { min: 25, name: 'Mozilla Firefox' },
    safari: { min: 6.1, name: 'Apple Safari' },
    edge: { min: 12, name: 'Microsoft Edge' },
    opera: { min: 15, name: 'Opera' }
  };

  // ============================================
  // DÉTECTION DU NAVIGATEUR
  // ============================================
  function getBrowserInfo() {
    const ua = navigator.userAgent;
    const browsers = [
      { name: 'chrome', regex: /Chrome\/(\d+)/, full: 'Google Chrome' },
      { name: 'firefox', regex: /Firefox\/(\d+)/, full: 'Mozilla Firefox' },
      { name: 'safari', regex: /Version\/(\d+).*Safari/, full: 'Apple Safari' },
      { name: 'edge', regex: /Edg(?:e)?\/(\d+)/, full: 'Microsoft Edge' },
      { name: 'opera', regex: /OPR\/(\d+)/, full: 'Opera' }
    ];

    for (const browser of browsers) {
      const match = ua.match(browser.regex);
      if (match) {
        return {
          name: browser.name,
          fullName: browser.full,
          version: parseInt(match[1]),
          ua: ua
        };
      }
    }

    return {
      name: 'unknown',
      fullName: 'Navigateur inconnu',
      version: 0,
      ua: ua
    };
  }

  // ============================================
  // VÉRIFICATION WEB AUDIO API
  // ============================================
  function checkWebAudioAPI() {
    return !!(
      window.AudioContext ||
      window.webkitAudioContext ||
      window.mozAudioContext
    );
  }

  // ============================================
  // VÉRIFICATION COMPATIBILITÉ
  // ============================================
  function checkCompatibility() {
    const browser = getBrowserInfo();
    const hasWebAudio = checkWebAudioAPI();

    // Vérifier si le navigateur est supporté
    const supportedBrowser = SUPPORTED_BROWSERS[browser.name];

    if (!supportedBrowser) {
      return {
        compatible: false,
        reason: 'unsupported_browser',
        browser: browser,
        message: `⚠️ Votre navigateur (${browser.fullName}) n'est pas optimisé pour Vapi. Veuillez utiliser Chrome, Firefox, Safari, Edge ou Opera.`,
        suggestion: 'Téléchargez Google Chrome pour la meilleure expérience.'
      };
    }

    if (browser.version < supportedBrowser.min) {
      return {
        compatible: false,
        reason: 'old_version',
        browser: browser,
        message: `⚠️ Votre ${browser.fullName} v${browser.version} est trop ancien. Version minimum: ${supportedBrowser.min}`,
        suggestion: `Mettez à jour ${browser.fullName} vers la version ${supportedBrowser.min} ou plus récente.`
      };
    }

    if (!hasWebAudio) {
      return {
        compatible: false,
        reason: 'no_web_audio',
        browser: browser,
        message: `⚠️ Votre navigateur ne supporte pas la Web Audio API. Veuillez mettre à jour votre navigateur.`,
        suggestion: 'Mettez à jour vers la dernière version de votre navigateur.'
      };
    }

    // Vérifier les extensions qui pourraient bloquer l'audio (Safari)
    if (browser.name === 'safari') {
      return {
        compatible: 'warning',
        reason: 'safari_extensions',
        browser: browser,
        message: `ℹ️ Sur Safari, désactivez les extensions de bloqueurs qui pourraient interferer avec l'audio.`,
        suggestion: 'Vérifiez que les extensions "Content Blockers" sont désactivées.'
      };
    }

    return {
      compatible: true,
      reason: 'ok',
      browser: browser,
      message: `✅ Votre navigateur est compatible avec Vapi!`,
      suggestion: null
    };
  }

  // ============================================
  // AFFICHAGE AVERTISSEMENT MODAL
  // ============================================
  function showCompatibilityWarning(result) {
    const container = document.createElement('div');
    container.id = 'vapi-compatibility-warning';
    container.innerHTML = `
      <style>
        #vapi-compatibility-warning {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(26, 26, 26, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99998;
          padding: 20px;
          font-family: 'Lora', Georgia, serif;
        }

        .compatibility-modal {
          background: #FFFFFF;
          border-radius: 16px;
          padding: 40px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 25px 50px rgba(26, 26, 26, 0.3);
          animation: modalSlideIn 0.4s ease;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .modal-icon {
          font-size: 48px;
          margin-bottom: 20px;
          text-align: center;
        }

        .modal-title {
          font-family: 'Playfair Display', serif;
          font-size: 24px;
          color: #1A1A1A;
          margin: 0 0 16px 0;
          text-align: center;
        }

        .modal-message {
          font-size: 15px;
          color: #666;
          line-height: 1.6;
          margin-bottom: 20px;
          text-align: center;
        }

        .modal-suggestion {
          background: #fffaf0;
          border-left: 4px solid #D4AF37;
          padding: 15px;
          border-radius: 4px;
          font-size: 13px;
          color: #856404;
          margin-bottom: 25px;
          line-height: 1.5;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
          flex-direction: column;
        }

        .modal-btn {
          padding: 14px 20px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Lora', serif;
        }

        .btn-primary {
          background: linear-gradient(135deg, #D4AF37 0%, #c9a227 100%);
          color: #1A1A1A;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(212, 175, 55, 0.35);
        }

        .btn-secondary {
          background: transparent;
          color: #D4AF37;
          border: 2px solid #D4AF37;
        }

        .btn-secondary:hover {
          background: #D4AF37;
          color: #1A1A1A;
        }

        .browser-info {
          background: #f5f5f5;
          padding: 12px;
          border-radius: 6px;
          font-size: 12px;
          color: #999;
          margin-top: 20px;
          text-align: center;
        }

        @media (max-width: 600px) {
          .compatibility-modal {
            padding: 30px 20px;
          }

          .modal-title {
            font-size: 20px;
          }

          .modal-message {
            font-size: 14px;
          }
        }
      </style>

      <div class="compatibility-modal">
        <div class="modal-icon" id="modal-icon">⚠️</div>
        <h2 class="modal-title" id="modal-title">Compatibilité navigateur</h2>
        <p class="modal-message" id="modal-message"></p>
        <div class="modal-suggestion" id="modal-suggestion"></div>
        <div class="modal-buttons">
          <button class="modal-btn btn-primary" id="btn-continue">
            Continuer quand même
          </button>
          <button class="modal-btn btn-secondary" id="btn-download">
            Télécharger un navigateur compatible
          </button>
        </div>
        <div class="browser-info">
          ${result.browser.fullName} v${result.browser.version}
        </div>
      </div>
    `;

    document.body.appendChild(container);

    // Remplir le contenu
    const messageEl = document.getElementById('modal-message');
    const suggestionEl = document.getElementById('modal-suggestion');
    const iconEl = document.getElementById('modal-icon');
    const titleEl = document.getElementById('modal-title');

    messageEl.textContent = result.message;
    suggestionEl.textContent = result.suggestion || '';

    if (result.reason === 'ok') {
      iconEl.textContent = '✅';
      titleEl.textContent = 'Parfait!';
      suggestionEl.style.display = 'none';
      document.getElementById('btn-continue').style.display = 'none';
      document.getElementById('btn-download').textContent = 'Continuer';
    } else if (result.compatible === 'warning') {
      iconEl.textContent = 'ℹ️';
      titleEl.textContent = 'Conseil';
    } else {
      iconEl.textContent = '❌';
      titleEl.textContent = 'Navigateur non compatible';
    }

    // Bouton continuer
    document.getElementById('btn-continue').addEventListener('click', () => {
      container.remove();
      console.log('✅ Utilisateur continue avec navigateur non optimal');
    });

    // Bouton télécharger
    document.getElementById('btn-download').addEventListener('click', () => {
      if (result.reason === 'ok') {
        container.remove();
      } else {
        window.open('https://www.google.com/chrome/', '_blank');
      }
    });

    // Log le résultat
    console.log('🌐 Browser Compatibility Check:', result);
  }

  // ============================================
  // INIT AU DÉMARRAGE
  // ============================================
  function init() {
    const result = checkCompatibility();

    // Afficher le message dans la console
    if (result.compatible === true) {
      console.log('✅ Browser compatible:', result.browser.fullName, `v${result.browser.version}`);
      console.log('✅ Web Audio API supported');
    } else if (result.compatible === 'warning') {
      console.warn('⚠️ Browser warning:', result.message);
      showCompatibilityWarning(result);
    } else {
      console.error('❌ Browser incompatible:', result.message);
      showCompatibilityWarning(result);
    }

    // Ajouter une bannière discrète si juste une warning
    if (result.compatible === 'warning') {
      addDiscreetBanner(result);
    }
  }

  // ============================================
  // BANNIÈRE DISCRÈTE
  // ============================================
  function addDiscreetBanner(result) {
    const banner = document.createElement('div');
    banner.innerHTML = `
      <style>
        #vapi-discreet-banner {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: #fffaf0;
          border: 1px solid #ffc107;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 13px;
          color: #856404;
          z-index: 9999;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-family: 'Lora', serif;
          max-width: 280px;
          animation: slideUp 0.4s ease;
        }

        #vapi-discreet-banner button {
          margin-left: 10px;
          background: transparent;
          border: none;
          color: #856404;
          cursor: pointer;
          font-weight: 600;
          text-decoration: underline;
          font-size: 12px;
        }

        #vapi-discreet-banner button:hover {
          color: #D4AF37;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 600px) {
          #vapi-discreet-banner {
            bottom: 15px;
            right: 15px;
            max-width: 250px;
          }
        }
      </style>
      <div id="vapi-discreet-banner">
        <strong>ℹ️ Conseil audio:</strong> ${result.message}
        <button onclick="document.getElementById('vapi-discreet-banner').remove()">✕</button>
      </div>
    `;
    document.body.appendChild(banner);
  }

  // Lancer au chargement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exporter pour accès externe
  window.VapiBrowserCheck = {
    getCompatibility: checkCompatibility,
    getBrowserInfo: getBrowserInfo,
    checkWebAudioAPI: checkWebAudioAPI
  };
})();